import React, { useEffect, useState } from 'react';
import './consent-demo.css';

export function ConsentDemoPresets() {
  const presets = window.MetaRouterConsentDemo?.presets || [];
  const [selected, setSelected] = useState(presets[0]?.id || '');
  if (!presets.length) return null;
  return (
    <section className="customize-card consent-demo-presets">
      <h2>Consent API simulations</h2>
      <p>
        Exercise AJS adapters without a vendor account. These are simulated browser APIs, not real CMP banners or SDKs.
      </p>
      <label>
        Consent simulation preset
        <select value={selected} onChange={event => setSelected(event.target.value)}>
          {presets.map(preset => (
            <option key={preset.id} value={preset.id}>
              {preset.label}
            </option>
          ))}
        </select>
      </label>
      <p>
        Each preset uses its matching published AJS bundle and bypasses saved uploads, custom scripts, Google mode, and
        the cookie/TCF controls.
      </p>
      <a href={`/shop?consentDemo=${encodeURIComponent(selected)}`}>Start consent simulation</a>
    </section>
  );
}

export function ConsentDemoPanel() {
  const demo = window.MetaRouterConsentDemo;
  const simulation = demo?.active;
  const [values, setValues] = useState(() => simulation?.getConsent() || {});
  const [error, setError] = useState(demo?.error);
  useEffect(() => {
    const onError = () => setError(demo?.error);
    window.addEventListener('mr:consent-demo:error', onError);
    return () => window.removeEventListener('mr:consent-demo:error', onError);
  }, [demo]);
  if (!demo?.requested) return null;

  const apply = next => {
    simulation.setConsent(next);
    setValues(simulation.getConsent());
  };
  const setAll = granted => apply(Object.fromEntries(simulation.preset.codes.map(code => [code, granted])));
  const setCategory = (code, value) => {
    const next = { ...values };
    if (value === 'default') delete next[code];
    else next[code] = value === 'Auto' ? 'Auto' : value === 'true';
    apply(next);
  };

  return (
    <aside className="consent-demo-panel" aria-label="Consent API simulation">
      <details open>
        <summary>Consent API simulation</summary>
        {error ? (
          <p role="alert">{error}</p>
        ) : (
          simulation && (
            <>
              <p>{simulation.preset.label}</p>
              <p>
                Simulated provider values below; AJS applies consent policy. GPC, when enabled in the browser, overrides
                advertising grants in these presets.
              </p>
              {simulation.preset.codes.map(code => (
                <label key={code}>
                  {code}
                  <select
                    aria-label={code}
                    value={String(values[code] ?? 'default')}
                    onChange={event => setCategory(code, event.target.value)}
                  >
                    <option value="true">Granted</option>
                    <option value="false">Denied</option>
                    {simulation.preset.provider === 'transcend' && <option value="Auto">Auto (AJS default)</option>}
                    {['custom', 'cookie'].includes(simulation.preset.provider) && (
                      <option value="default">Omitted (AJS default)</option>
                    )}
                  </select>
                </label>
              ))}
              <div className="consent-demo-actions">
                <button type="button" onClick={() => setAll(true)}>
                  Grant all
                </button>
                <button type="button" onClick={() => setAll(false)}>
                  Deny all
                </button>
                <button type="button" onClick={() => window.analytics?.track('Consent Simulation Check')}>
                  Send test event
                </button>
              </div>
              <p>
                <small>
                  Bundle: {simulation.preset.fileName}.js. Choices reset on reload; this is not a stored visitor
                  profile.
                </small>
              </p>
            </>
          )
        )}
        <a href="/shop">Exit simulation</a>
      </details>
    </aside>
  );
}
